import { supabase } from './supabaseClient';
import { API_ENDPOINTS, apiRequest } from '../config/api';
import { EmailService } from './emailService';

export interface RelevantDocument {
  id: number;
  title: string;
  analyst_name?: string;
  company_name?: string;
  sector_name?: string;
  created_at: string;
  file_path?: string;
  summary?: string;
}

export interface EmailRequest {
  client_id: number;
  document_id: number;
  client_email: string;
  client_name: string;
  language?: string;
}

export class DocumentService {
  // Get documents relevant to client interests
  static async getRelevantDocuments(clientId: number): Promise<RelevantDocument[]> {
    try {
      // First, get client interests
      const { data: interests, error: interestsError } = await supabase
        .from('client_interests')
        .select(`
          sector_id,
          company_id,
          sector_name
        `)
        .eq('client_id', clientId);

      if (interestsError) throw interestsError;

      if (!interests || interests.length === 0) {
        return [];
      }

      // Build query to find relevant documents (V2 tables)
      let query = supabase
        .from('documents')
        .select(`
          id,
          title,
          created_at,
          storage_url,
          company_id,
          sector_id,
          primary_ticker
        `)
        .order('created_at', { ascending: false })
        .limit(20); // Get latest 20 relevant documents

      // Add filters based on interests
      const sectorIds = interests.filter(i => i.sector_id).map(i => i.sector_id);
      const companyIds = interests.filter(i => i.company_id).map(i => i.company_id);

      if (sectorIds.length > 0 || companyIds.length > 0) {
        const orConditions = [];
        
        if (sectorIds.length > 0) {
          orConditions.push(`sector_id.in.(${sectorIds.join(',')})`);
        }
        
        if (companyIds.length > 0) {
          orConditions.push(`company_id.in.(${companyIds.join(',')})`);
        }

        if (orConditions.length > 0) {
          query = query.or(orConditions.join(','));
        }
      }

      const { data: documents, error: documentsError } = await query;

      if (documentsError) throw documentsError;

      // Transform the data to match our interface
      return (documents || []).map(doc => ({
        id: doc.id,
        title: doc.title,
        analyst_name: undefined, // We'll fetch this separately if needed
        company_name: undefined, // We'll fetch this separately if needed
        sector_name: undefined, // We'll fetch this separately if needed
        created_at: doc.created_at,
        file_path: doc.storage_url // Use storage_url as file_path for compatibility
      }));

    } catch (error) {
      console.error('Error fetching relevant documents:', error);
      throw error;
    }
  }

  // Generate summary for a document using the direct PDF summary function
  static async generateDocumentSummary(documentId: number, language: string = 'English'): Promise<string> {
    try {
      console.log('🔍 Attempting to generate summary for document:', documentId);
      console.log('🔗 API Endpoint:', API_ENDPOINTS.pdfDirectSummary);
      
      const requestBody = {
        document_id: documentId,
        language: language
      };
      
      console.log('📤 Request body:', requestBody);
      
      const data = await apiRequest('/documents/pdf-direct-summary', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
      console.log('✅ Summary generated successfully:', data.summary?.substring(0, 100) + '...');
      return data.summary;

    } catch (error) {
      console.error('❌ Error generating summary:', error);
      throw error;
    }
  }

  // Send email with document summary
  static async sendDocumentEmail(emailRequest: EmailRequest): Promise<void> {
    try {
      // First generate the summary
      const summary = await this.generateDocumentSummary(
        emailRequest.document_id, 
        emailRequest.language || 'English'
      );

      // Get document details for the email
      const document = await this.getDocumentDetails(emailRequest.document_id);
      const documentTitle = document?.title || 'Research Document';
      const pdfUrl = document?.file_path; // This contains the storage_url

      // Send email using EmailService
      await EmailService.sendDocumentSummaryEmail(emailRequest, summary, documentTitle, pdfUrl);

    } catch (error) {
      console.error('Error sending email:', error);
      throw error;
    }
  }

  // Get document details
  static async getDocumentDetails(documentId: number): Promise<RelevantDocument | null> {
    try {
      const { data, error } = await supabase
        .from('documents')
        .select(`
          id,
          title,
          created_at,
          storage_url,
          company_id,
          sector_id,
          primary_ticker
        `)
        .eq('id', documentId)
        .single();

      if (error) throw error;

      if (!data) return null;

      return {
        id: data.id,
        title: data.title,
        analyst_name: undefined, // We'll fetch this separately if needed
        company_name: undefined, // We'll fetch this separately if needed
        sector_name: undefined, // We'll fetch this separately if needed
        created_at: data.created_at,
        file_path: data.storage_url // Use storage_url as file_path for compatibility
      };

    } catch (error) {
      console.error('Error fetching document details:', error);
      throw error;
    }
  }
} 